import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import {
  Orgs,
  Proposals,
  Subscriptions,
  type OrgFiscal,
  type Proposal,
  type TaxIdType,
} from "../../lib/apiTyped";
import { useOrgSnapshot } from "../../hooks/useOrgSnapshot";
import { useApiCall } from "../../lib/useToastError";
import { PageHeader } from "../../components/AppLayout";
import { Card } from "../../components/shared/Card";
import { FormInput } from "../../components/shared/FormInput";
import { Button } from "../../components/shared/Button";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Spinner } from "../../components/shared/Spinner";
import { fmtEur, fmtDate } from "../../lib/format";

// Single page that owns every billing concern: current subscription
// summary, pending one-time payments, the org's tax identity (legal
// name, address, tax id, billing email) and the historical Stripe
// invoices (subscription cycles + paid one-offs together). Replaces the
// old /settings/fiscal and /payments/proposals pages.

const COUNTRIES: Array<[string, string]> = [
  ["ES", "Spain"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["IT", "Italy"],
  ["PT", "Portugal"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["IE", "Ireland"],
  ["AT", "Austria"],
  ["LU", "Luxembourg"],
  ["DK", "Denmark"],
  ["FI", "Finland"],
  ["SE", "Sweden"],
  ["GB", "United Kingdom"],
  ["CH", "Switzerland"],
  ["NO", "Norway"],
  ["US", "United States"],
];

const TAX_ID_OPTIONS: Array<[TaxIdType, string]> = [
  ["es_cif", "Spanish CIF"],
  ["es_dni", "Spanish DNI / NIF"],
  ["eu_vat", "European VAT number"],
  ["gb_vat", "UK VAT number"],
  ["ch_vat", "Swiss VAT number"],
  ["no_vat", "Norway VAT number"],
  ["us_ein", "US EIN"],
];

function defaultTaxIdType(country: string): TaxIdType {
  if (country === "ES") return "es_cif";
  if (country === "GB") return "gb_vat";
  if (country === "CH") return "ch_vat";
  if (country === "US") return "us_ein";
  if (country === "NO") return "no_vat";
  return "eu_vat";
}

export function OwnerBilling() {
  const { orgId, org, loading } = useOrgSnapshot();
  const { run } = useApiCall();
  const existing = (org as { fiscal?: OrgFiscal & { taxIdType?: TaxIdType } } | null)
    ?.fiscal;
  const consentRecorded = Boolean(
    (org as { fiscalConsent?: unknown } | null)?.fiscalConsent,
  );

  if (loading || !orgId) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Billing"
        subtitle="Subscription, invoices, billing details and Tax ID in one place."
      />

      <div className="space-y-6">
        <SubscriptionSummary orgId={orgId} />
        <PendingPayments orgIdUnused={orgId} />
        <InvoicesList orgId={orgId} hasFiscal={Boolean(existing)} />
        <BillingEmailSection
          orgId={orgId}
          existing={existing}
          consentRecorded={consentRecorded}
          onSaved={() => run(() => Promise.resolve(null))}
        />
        <BillingAddressSection
          orgId={orgId}
          existing={existing}
          consentRecorded={consentRecorded}
        />
        <TaxIdSection
          orgId={orgId}
          existing={existing}
          consentRecorded={consentRecorded}
        />
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Subscription summary

function SubscriptionSummary({ orgId }: { orgId: string }) {
  const [data, setData] = useState<Awaited<
    ReturnType<typeof Subscriptions.get>
  > | null>(null);
  useEffect(() => {
    let cancelled = false;
    Subscriptions.get(orgId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        /* noop */
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-black text-ink">Subscription</h2>
        <Link
          to="/api/subscription"
          className="text-xs text-ink-2 hover:text-ink underline"
        >
          Manage
        </Link>
      </div>
      {!data ? (
        <p className="text-sm text-ink-3">—</p>
      ) : !data.activeTier ? (
        <p className="text-sm text-ink-2">No plan assigned.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
          <div>
            <div className="text-xs text-ink-3 uppercase tracking-wider">
              Plan
            </div>
            <div className="text-ink font-semibold">
              {data.tier?.name ?? data.activeTier}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-3 uppercase tracking-wider">
              Per seat
            </div>
            <div className="text-ink font-semibold">
              {fmtEur(data.tier?.pricePerSeatEur ?? 0)} / month
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-3 uppercase tracking-wider">
              Seats
            </div>
            <div className="text-ink font-semibold">{data.seatCount}</div>
          </div>
          <div>
            <div className="text-xs text-ink-3 uppercase tracking-wider">
              Status
            </div>
            <StatusBadge status={data.subscriptionStatus ?? "pending"} />
          </div>
        </div>
      )}
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Pending one-time payments (proposals waiting to be paid)

function PendingPayments({ orgIdUnused }: { orgIdUnused: string }) {
  void orgIdUnused;
  const [pending, setPending] = useState<Proposal[]>([]);
  useEffect(() => {
    let cancelled = false;
    Proposals.list()
      .then((res) => {
        if (cancelled) return;
        setPending(res.proposals.filter((p) => p.status === "sent"));
      })
      .catch(() => {
        /* noop */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  if (pending.length === 0) return null;
  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink mb-4">Pending payments</h2>
      <ul className="divide-y divide-border">
        {pending.map((p) => (
          <li key={p.id} className="py-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-ink">{p.concept}</div>
              <div className="text-xs text-ink-3">{fmtDate(p.createdAt)}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-ink font-semibold">
                {fmtEur(p.amountEur * (1 + (p.ivaRate ?? 0) / 100))}
              </span>
              {p.paymentLink ? (
                <a
                  href={p.paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-ink text-white text-xs font-semibold rounded-full px-3 py-1.5 hover:opacity-80"
                >
                  Pay now
                </a>
              ) : (
                <StatusBadge status={p.status} />
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Invoices — subscription cycles + paid one-offs together

function InvoicesList({
  orgId,
  hasFiscal,
}: {
  orgId: string;
  hasFiscal: boolean;
}) {
  const [data, setData] = useState<Awaited<
    ReturnType<typeof Orgs.invoices>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!hasFiscal) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Orgs.invoices(orgId)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, hasFiscal]);

  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink mb-4">Invoices</h2>
      {!hasFiscal ? (
        <p className="text-sm text-ink-3">
          Complete your billing details to start receiving invoices.
        </p>
      ) : loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 text-ink-3 animate-spin" />
        </div>
      ) : !data || data.invoices.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-sm font-semibold text-ink mb-1">No invoices yet</div>
          <p className="text-xs text-ink-3">
            When a billing cycle or payment completes, you'll see it here.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {data.invoices.map((inv) => (
            <li
              key={inv.id}
              className="py-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm text-ink truncate">
                  {inv.description}
                </div>
                <div className="text-xs text-ink-3 flex flex-wrap gap-2">
                  <span>{inv.number ?? inv.id.slice(0, 8)}</span>
                  <span>·</span>
                  <span>{inv.kind === "subscription" ? "Subscription" : "One-time"}</span>
                  {inv.paidAt && (
                    <>
                      <span>·</span>
                      <span>{fmtDate(inv.paidAt)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-ink font-semibold">
                  {fmtEur(inv.total)}
                </span>
                <StatusBadge status={inv.status} />
                {inv.pdfUrl && (
                  <a
                    href={inv.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink-2 hover:text-ink"
                    title="Download PDF"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                )}
                {inv.hostedUrl && (
                  <a
                    href={inv.hostedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink-2 hover:text-ink"
                    title="View in Stripe"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Billing email section

function BillingEmailSection({
  orgId,
  existing,
  consentRecorded,
}: {
  orgId: string;
  existing: (OrgFiscal & { taxIdType?: TaxIdType }) | undefined;
  consentRecorded: boolean;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(existing?.emailFacturacion ?? "");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();
  useEffect(() => {
    setEmail(existing?.emailFacturacion ?? "");
  }, [existing?.emailFacturacion]);
  if (!existing) return null;
  async function save() {
    if (!existing) return;
    setBusy(true);
    await run(
      () =>
        Orgs.updateFiscal(
          orgId,
          { ...existing, emailFacturacion: email } as OrgFiscal,
          { consent: consentRecorded ? undefined : { version: "v1", accepted: true } },
        ),
      { successMessage: "Details saved." },
    );
    setBusy(false);
  }
  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink">Billing email</h2>
      <p className="text-sm text-ink-3 mb-4">
        Invoices will be sent automatically to this email.
      </p>
      <FormInput label="Email" value={email} onChange={setEmail} type="email" />
      <div className="mt-4">
        <Button
          variant="primary"
          onClick={save}
          disabled={busy || email === existing.emailFacturacion}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Billing address section

function BillingAddressSection({
  orgId,
  existing,
  consentRecorded,
}: {
  orgId: string;
  existing: (OrgFiscal & { taxIdType?: TaxIdType }) | undefined;
  consentRecorded: boolean;
}) {
  const [razonSocial, setRazonSocial] = useState(existing?.razonSocial ?? "");
  const [pais, setPais] = useState(existing?.pais ?? "ES");
  const [line1, setLine1] = useState(existing?.direccionFiscal.line1 ?? "");
  const [line2, setLine2] = useState(existing?.direccionFiscal.line2 ?? "");
  const [city, setCity] = useState(existing?.direccionFiscal.city ?? "");
  const [postalCode, setPostalCode] = useState(
    existing?.direccionFiscal.postalCode ?? "",
  );
  const [region, setRegion] = useState(existing?.direccionFiscal.region ?? "");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  useEffect(() => {
    if (!existing) return;
    setRazonSocial(existing.razonSocial);
    setPais(existing.pais);
    setLine1(existing.direccionFiscal.line1);
    setLine2(existing.direccionFiscal.line2 ?? "");
    setCity(existing.direccionFiscal.city);
    setPostalCode(existing.direccionFiscal.postalCode);
    setRegion(existing.direccionFiscal.region ?? "");
  }, [existing]);

  const needsConsent = !consentRecorded;
  const canSave = !busy && (!needsConsent || consent);

  async function save() {
    if (!canSave) return;
    setBusy(true);
    const fiscal: OrgFiscal = {
      nif: existing?.nif ?? "",
      taxIdType: existing?.taxIdType,
      razonSocial,
      direccionFiscal: {
        line1,
        line2: line2 || null,
        city,
        postalCode,
        region: region || null,
      },
      pais,
      emailFacturacion: existing?.emailFacturacion ?? "",
    };
    await run(
      () =>
        Orgs.updateFiscal(orgId, fiscal, {
          consent: needsConsent ? { version: "v1", accepted: true } : undefined,
        }),
      { successMessage: "Details saved." },
    );
    setBusy(false);
  }

  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink">Billing address</h2>
      <p className="text-sm text-ink-3 mb-4">
        Will appear on every invoice issued from Stripe.
      </p>
      <div className="space-y-4">
        <FormInput
          label="Legal name"
          value={razonSocial}
          onChange={setRazonSocial}
        />
        <div>
          <label className="block text-xs font-semibold mb-1 text-ink-3">
            Country or region
          </label>
          <select
            value={pais}
            onChange={(e) => setPais(e.target.value)}
            className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none"
            style={{
              backgroundColor: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(0,0,0,0.14)",
              color: "#0f0f0f",
            }}
          >
            {COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <FormInput label="Address line 1" value={line1} onChange={setLine1} />
        <FormInput
          label="Address line 2 (optional)"
          value={line2}
          onChange={setLine2}
        />
        <div className="grid grid-cols-2 gap-3">
          <FormInput label="City" value={city} onChange={setCity} />
          <FormInput
            label="Postal code"
            value={postalCode}
            onChange={setPostalCode}
          />
        </div>
        <FormInput
          label="Region (optional)"
          value={region}
          onChange={setRegion}
        />
      </div>

      {needsConsent && (
        <div className="mt-5 pt-5 border-t border-border">
          <label className="flex items-start gap-3 cursor-pointer text-sm text-ink-2">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-ink shrink-0"
            />
            <span>
              I agree that this data is processed in accordance with the{" "}
              <Link
                to="/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-ink hover:opacity-80"
              >
                Privacy Policy
              </Link>{" "}
              to issue invoices and create my customer profile in Stripe
              Payments Europe.
            </span>
          </label>
        </div>
      )}

      <div className="mt-5">
        <Button variant="primary" onClick={save} disabled={!canSave}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Tax ID section

function TaxIdSection({
  orgId,
  existing,
  consentRecorded,
}: {
  orgId: string;
  existing: (OrgFiscal & { taxIdType?: TaxIdType }) | undefined;
  consentRecorded: boolean;
}) {
  const [type, setType] = useState<TaxIdType>(
    existing?.taxIdType ?? defaultTaxIdType(existing?.pais ?? "ES"),
  );
  const [value, setValue] = useState(existing?.nif ?? "");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  useEffect(() => {
    setType(existing?.taxIdType ?? defaultTaxIdType(existing?.pais ?? "ES"));
    setValue(existing?.nif ?? "");
  }, [existing?.taxIdType, existing?.pais, existing?.nif]);

  const needsConsent = !consentRecorded;

  async function save() {
    if (!existing || busy) return;
    setBusy(true);
    const fiscal: OrgFiscal = {
      ...existing,
      nif: value,
      taxIdType: type,
    };
    await run(
      () =>
        Orgs.updateFiscal(orgId, fiscal, {
          consent: needsConsent ? { version: "v1", accepted: true } : undefined,
        }),
      { successMessage: "Tax ID saved." },
    );
    setBusy(false);
  }

  if (!existing) return null;

  return (
    <Card className="p-6">
      <h2 className="text-lg font-black text-ink">Tax ID</h2>
      <p className="text-sm text-ink-3 mb-4">
        Appears as the tax identifier on every invoice.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as TaxIdType)}
          className="rounded-xl px-3 py-2.5 text-sm focus:outline-none sm:w-56"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(0,0,0,0.14)",
            color: "#0f0f0f",
          }}
        >
          {TAX_ID_OPTIONS.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={type === "es_cif" ? "B12345678" : "DE123456789"}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          style={{
            backgroundColor: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(0,0,0,0.14)",
            color: "#0f0f0f",
          }}
        />
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          onClick={save}
          disabled={
            busy ||
            (value === existing.nif &&
              type === (existing.taxIdType ?? defaultTaxIdType(existing.pais)))
          }
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
}
